import { HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from 'rxjs';

import { AuthService } from '../data-access/auth.service';

// List of routes to ignore
const ignoredRoutes: string[] = [];

/**
 * Interceptor to handle unauthorized HTTP responses.
 *
 * This interceptor catches HTTP responses with a status code of 401 (Unauthorized).
 * When such a response is encountered, it triggers a new login process via the AuthService.
 *
 * Note:
 * In case of the ToolLibrary app we receive currently a 401 response when a call is made to the admin backend
 * (for e.g. fetching the users profile).
 *
 * However, this triggers currently not the logout process, which is a side effect and actually the wanted behavior.
 * Most likely the reason for this is, that the CORS setting of the admin backend doesn't include the ToolLibrary frontend URL.
 * Because of that the preflight request fails the received code with a status code `0` and so not catch.
 *
 * If that changes the interceptor should be updated to ignore the 401 responses from the admin backend similar in how the
 * with-credentials interceptor handles it.
 *
 * From UX point of view it could be as well considered to show a message to the user that the session is expired and
 * handle that in a more user friendly way. But this should be done after the final decision about the BFF approach are made.
 */
export const withUnauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Check if the request URL matches any of the ignored routes
  const shouldIgnore = ignoredRoutes.some(route => req.url.includes(route));
  if (shouldIgnore) {
    return next(req);
  }

  return next(req).pipe(
    catchError(error => {
      if (error.status === HttpStatusCode.Unauthorized) {
        authService.login();
      }
      throw error;
    })
  );
};
